import {throttle} from '@shopify/marketing-assets';

// ScaleContentsLikeImage requires an object reference to a container element. That container
// element should be assigned a CSS width that matches the size it is meant to be displayed at -
// usually matching the width of the element in the design file. JS will then create a nested
// container with a fixed width matching the given default container pixel width and then use CSS
// transform to scale that element down to fit in the container. Those contents will be positioned
// absolutely since transformed elements still take up their original size and positioning, and a
// new placeholder element will be added that takes up the same proportions that the given
// contents would. In addition, the placeholder element sizing will be recalculated any time the
// screen is resized or an image element that is a child of the container loads.

// For example, $elem is passed in measuring 200px by 100px, while the container it is nested inside
//  is at a width of 50px (25% of the $elem width). Contents of $elem will be nested into a newly
//  created, absolutely positioned wrapper with an assigned CSS property of 'transform: scale(0.25)'.
//  A second empty placeholder element will be positioned relatively and be given a padding bottom of
//  50% (100px height / 200px width) in order to hold the position that the scaled content will
//  appear in.

export class ScaleContentLikeImage {
  constructor($elem) {
    this.$elem = $elem;
    this._handleVisiblityChange = this._handleVisiblityChange.bind(this);

    if (this._contentIsVisible()) {
      this._init();
    } else {
      this.observer = this._observeVisibility(this.$elem, this._handleVisiblityChange);
    }
  }

  _contentIsVisible() {
    return this.$elem.offsetHeight > 0;
  }

  _handleVisiblityChange(visible) {
    if (!visible) { return; }

    this.observer.disconnect();
    this._init();
  }

  // enables JS test stubs since getComputedStyle in jsDom always returns 100 when width is set to 100%
  _elemWidth() {
    return this.$elem.offsetWidth;
  }

  _init() {
    this.originalWidth = parseInt(getComputedStyle(this.$elem).width, 10);
    this.$elem.style.width = '100%';

    this.$scalingElem = this._createScalingElem();
    this._nestContentInScalingContainer();

    this._onResize = throttle(this._updateTransform.bind(this), 100, true);
    this._onImageLoad = throttle(this._onImageLoad.bind(this), 500, true);

    this._updateTransform();

    this._registerImages();
    window.addEventListener('resize', this._onResize);
  }

  _registerImages() {
    this.$elem.querySelectorAll('img').forEach((img) => {
      img.addEventListener('load', this._onImageLoad);
    });
  }

  _onImageLoad() {
    this._updatePlaceholderSize();
  }

  _nestContentInScalingContainer() {
    // move children from original container to the new scaling container
    while (this.$elem.hasChildNodes()) {
      this.$scalingElem.appendChild(this.$elem.firstChild);
    }

    // nest the scaling container in the original container
    this.$elem.append(this.$scalingElem);
    this.$elem.append(this._createPlaceholderElem());
  }

  _heightToWidthRatio() {
    return this.$scalingElem.offsetHeight / this.$scalingElem.offsetWidth * 100;
  }

  _createPlaceholderElem() {
    this.$placeholderElem = document.createElement('div');
    this.$placeholderElem.style.setProperty('height', '0');
    this._updatePlaceholderSize();
    return this.$placeholderElem;
  }

  _createScalingElem() {
    const $scalingElem = document.createElement('div');
    $scalingElem.style.setProperty('width', `${this.originalWidth}px`);
    $scalingElem.style.setProperty('position', 'absolute');
    $scalingElem.style.transformOrigin = '0 0';

    return $scalingElem;
  }

  _containerSizeAsPercentageOfBase() {
    return this._elemWidth() / this.originalWidth;
  }

  _updatePlaceholderSize() {
    this.$placeholderElem.style.setProperty('padding-bottom', `${this._heightToWidthRatio()}%`);
  }

  _updateTransform() {
    const relativeSize = this._containerSizeAsPercentageOfBase();
    this.$scalingElem.style.setProperty('transform', `scale(${relativeSize})`);
  }

  // borrowed from https://stackoverflow.com/questions/1462138/event-listener-for-when-element-becomes-visible
  _observeVisibility(element, callback) {
    const options = {root: document.documentElement};

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        callback(entry.intersectionRatio > 0);
      });
    }, options);

    observer.observe(element);

    return observer;
  }
}
